import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Badge, Button, Spinner, ProgressBar,
  Form, Alert, OverlayTrigger, Tooltip, Tab, Tabs
} from 'react-bootstrap';
import {
  FaArrowLeft, FaProjectDiagram, FaLayerGroup, FaThermometerHalf,
  FaShieldAlt, FaStar, FaHeart, FaChartBar, FaHistory,
  FaExclamationTriangle, FaDatabase, FaClock, FaEye,
  FaSitemap, FaCheckCircle, FaTimesCircle, FaBalanceScale,
  FaChartLine, FaFingerprint, FaInfoCircle, FaSyncAlt
} from 'react-icons/fa';
import knowledgeBaseApi from '../../services/knowledgeBaseApi';

// ── Helpers ──
const scoreColor = (val) => {
  const pct = (val || 0) * 100;
  if (pct >= 75) return '#22c55e';
  if (pct >= 50) return '#eab308';
  if (pct >= 25) return '#f97316';
  return '#ef4444';
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

// ── Score Card ──
const ScoreCard = ({ label, value, color, format = 'pct' }) => (
  <div className="cd-score-card">
    <div className="cd-score-label">{label}</div>
    <div className="cd-score-value" style={{ color: color || scoreColor(value) }}>
      {format === 'pct' ? pct(value) : format === 'num' ? (value || 0) : value}
    </div>
  </div>
);

// ── Histogram ──
const Histogram = ({ title, buckets, counts }) => {
  const max = Math.max(...counts, 1);
  return (
    <div className="cd-histogram">
      <div className="cd-histogram-title">{title}</div>
      <div className="cd-histogram-bars">
        {counts.map((count, i) => (
          <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{buckets[i]}: {count}</Tooltip>}>
            <div className="cd-histogram-col">
              <div className="cd-histogram-bar" style={{ height: `${(count / max) * 100}%` }} />
              <div className="cd-histogram-label">{i % 2 === 0 ? buckets[i]?.split('-')[0] : ''}</div>
            </div>
          </OverlayTrigger>
        ))}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN DETAIL VIEW
// ══════════════════════════════════════════════════════════
const ClusterDetailView = ({ clusterId, onBack, setError, setSuccess }) => {
  const [cluster, setCluster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Lazy-loaded data
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersPagination, setMembersPagination] = useState({});
  const [membersPage, setMembersPage] = useState(1);
  const [memberSort, setMemberSort] = useState('importance');

  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [scoreDistribution, setScoreDistribution] = useState(null);
  const [scoreDistLoading, setScoreDistLoading] = useState(false);

  const [sourceBreakdown, setSourceBreakdown] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  const [classBreakdown, setClassBreakdown] = useState(null);
  const [classLoading, setClassLoading] = useState(false);

  const [calibration, setCalibration] = useState(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);

  const [conflicts, setConflicts] = useState(null);
  const [conflictsLoading, setConflictsLoading] = useState(false);

  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const [evolution, setEvolution] = useState(null);
  const [evolutionLoading, setEvolutionLoading] = useState(false);

  const [hierarchy, setHierarchy] = useState(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  // ── Load cluster ──
  const loadCluster = useCallback(async () => {
    setLoading(true);
    try {
      const data = await knowledgeBaseApi.clusters.get(clusterId, {
        include_members: false,
        include_scores: true,
        include_source_breakdown: false,
      });
      setCluster(data?.cluster || null);
    } catch (err) {
      setError?.('Failed to load cluster: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [clusterId, setError]);

  useEffect(() => { loadCluster(); }, [loadCluster]);

  // ── Lazy loaders ──
  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const data = await knowledgeBaseApi.clusters.getMembers(clusterId, {
        sort_by: memberSort,
        page: membersPage,
        page_size: 30,
      });
      setMembers(data?.members || []);
      setMembersPagination(data?.pagination || {});
    } catch (err) { console.error(err); }
    finally { setMembersLoading(false); }
  }, [clusterId, memberSort, membersPage]);

  const loadHealth = async () => {
    setHealthLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getHealthReport(clusterId); setHealthReport(d?.health_report || null); }
    catch (err) { console.error(err); }
    finally { setHealthLoading(false); }
  };

  const loadScoreDist = async () => {
    setScoreDistLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getScoreDistribution(clusterId); setScoreDistribution(d); }
    catch (err) { console.error(err); }
    finally { setScoreDistLoading(false); }
  };

  const loadSources = async () => {
    setSourceLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getSourceBreakdown(clusterId); setSourceBreakdown(d?.source_breakdown || []); }
    catch (err) { console.error(err); }
    finally { setSourceLoading(false); }
  };

  const loadClassifications = async () => {
    setClassLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getClassificationBreakdown(clusterId); setClassBreakdown(d?.classification_breakdown || []); }
    catch (err) { console.error(err); }
    finally { setClassLoading(false); }
  };

  const loadCalibration = async () => {
    setCalibrationLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getCalibrationSummary(clusterId, 30); setCalibration(d?.calibration_summary || null); }
    catch (err) { console.error(err); }
    finally { setCalibrationLoading(false); }
  };

  const loadConflicts = async () => {
    setConflictsLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getConflictSummary(clusterId, 30); setConflicts(d); }
    catch (err) { console.error(err); }
    finally { setConflictsLoading(false); }
  };

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getTimeline(clusterId, 'week', 90); setTimeline(d?.timeline || []); }
    catch (err) { console.error(err); }
    finally { setTimelineLoading(false); }
  };

  const loadEvolution = async () => {
    setEvolutionLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getEvolution(clusterId); setEvolution(d?.evolution || null); }
    catch (err) { console.error(err); }
    finally { setEvolutionLoading(false); }
  };

  const loadHierarchy = async () => {
    setHierarchyLoading(true);
    try { const d = await knowledgeBaseApi.clusters.getHierarchy(clusterId); setHierarchy(d); }
    catch (err) { console.error(err); }
    finally { setHierarchyLoading(false); }
  };

  // ── Tab change triggers lazy load ──
  useEffect(() => {
    if (activeTab === 'members' && !members) loadMembers();
    if (activeTab === 'health' && !healthReport) loadHealth();
    if (activeTab === 'scores' && !scoreDistribution) loadScoreDist();
    if (activeTab === 'sources' && !sourceBreakdown) loadSources();
    if (activeTab === 'sources' && !classBreakdown) loadClassifications();
    if (activeTab === 'calibration' && !calibration) loadCalibration();
    if (activeTab === 'conflicts' && !conflicts) loadConflicts();
    if (activeTab === 'timeline' && !timeline) loadTimeline();
    if (activeTab === 'evolution' && !evolution) loadEvolution();
    if (activeTab === 'hierarchy' && !hierarchy) loadHierarchy();
  }, [activeTab]);

  // Reload members when sort/page changes
  useEffect(() => {
    if (activeTab === 'members') loadMembers();
  }, [memberSort, membersPage, loadMembers]);

  if (loading) {
    return (
      <div className="cd-loading">
        <Spinner animation="border" variant="info" />
        <span>Loading cluster…</span>
      </div>
    );
  }

  if (!cluster) {
    return (
      <div className="cd-loading">
        <FaInfoCircle className="me-2" />
        <span>Cluster not found</span>
        <Button variant="outline-secondary" size="sm" onClick={onBack} className="ms-3">← Back</Button>
      </div>
    );
  }

  const ss = cluster.score_summary || {};

  return (
    <div className="cd-container">
      {/* ── Header ── */}
      <div className="cd-header">
        <div className="cd-header-left">
          <Button variant="outline-secondary" size="sm" onClick={onBack} className="cd-back-btn">
            <FaArrowLeft />
          </Button>
          <div>
            <h5 className="cd-cluster-name">{cluster.cluster_label || 'Unnamed Cluster'}</h5>
            <div className="cd-cluster-meta">
              <Badge className="cd-meta-badge">{cluster.cluster_category || 'general'}</Badge>
              <Badge className={`cd-status-badge cd-status-${cluster.status}`}>{cluster.status}</Badge>
              <span className="cd-meta-id"><FaFingerprint /> {clusterId.slice(0, 12)}</span>
            </div>
          </div>
        </div>
        <div className="cd-header-right">
          <Button variant="outline-info" size="sm" onClick={loadCluster}>
            <FaSyncAlt className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      {cluster.cluster_description && (
        <div className="cd-description">{cluster.cluster_description}</div>
      )}

      {/* ── Quick Stats ── */}
      <div className="cd-quick-stats">
        <ScoreCard label="Members" value={cluster.member_count} format="num" color="#3b82f6" />
        <ScoreCard label="Coherence" value={cluster.cluster_coherence_score} />
        <ScoreCard label="Stability" value={cluster.cluster_stability_score} />
        <ScoreCard label="Avg Quality" value={cluster.avg_member_quality} />
        <ScoreCard label="Avg Trust" value={ss.avg_trust} />
        <ScoreCard label="Avg Credibility" value={ss.avg_credibility} format="raw" color={scoreColor(Math.max(0, ss.avg_credibility || 0))} />
        <ScoreCard label="Avg Importance" value={ss.avg_importance} />
        <ScoreCard label="Evolutions" value={cluster.evolution_count} format="num" color="#8b5cf6" />
      </div>

      {/* ── Score Summary Warnings ── */}
      {(ss.negative_credibility_count > 0 || ss.needs_review_count > 0) && (
        <div className="cd-warnings">
          {ss.negative_credibility_count > 0 && (
            <Badge bg="danger" className="cd-warn-badge">
              <FaTimesCircle className="me-1" />{ss.negative_credibility_count} negative credibility
            </Badge>
          )}
          {ss.needs_review_count > 0 && (
            <Badge bg="warning" text="dark" className="cd-warn-badge">
              <FaExclamationTriangle className="me-1" />{ss.needs_review_count} need review
            </Badge>
          )}
        </div>
      )}

      {/* ── Tabs ── */}
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="cd-tabs">

        {/* MEMBERS TAB */}
        <Tab eventKey="members" title={<><FaLayerGroup className="me-1" /> Members</>}>
          <div className="cd-tab-content">
            <div className="cd-tab-toolbar">
              <Form.Select size="sm" className="cd-sort-select" value={memberSort} onChange={(e) => { setMemberSort(e.target.value); setMembersPage(1); }}>
                <option value="importance">Sort: Importance</option>
                <option value="credibility">Sort: Credibility</option>
                <option value="trust">Sort: Trust</option>
                <option value="membership">Sort: Membership</option>
                <option value="access">Sort: Access Count</option>
                <option value="created">Sort: Created</option>
              </Form.Select>
            </div>

            {membersLoading ? (
              <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div>
            ) : members && members.length > 0 ? (
              <>
                <div className="cd-members-list">
                  {members.map((m, i) => (
                    <div key={m.learning_id || i} className="cd-member-row">
                      <div className="cd-member-main">
                        <div className="cd-member-summary">{m.summary || m.preview || 'No summary'}</div>
                        <div className="cd-member-badges">
                          {m.learning_type && <Badge className="cd-type-badge">{m.learning_type}</Badge>}
                          {m.learning_classification && <Badge className="cd-class-badge">{m.learning_classification}</Badge>}
                          {m.is_primary_cluster && <Badge bg="info">Primary</Badge>}
                          {m.requires_review && <Badge bg="warning" text="dark">Review</Badge>}
                        </div>
                      </div>
                      <div className="cd-member-scores">
                        <span className="cd-member-score"><FaStar /> {pct(m.importance_score)}</span>
                        <span className="cd-member-score"><FaShieldAlt /> {pct(m.trust_score)}</span>
                        <span className="cd-member-score" style={{ color: scoreColor(Math.max(0, m.credibility_score || 0)) }}>
                          Cred {m.credibility_score?.toFixed(3)}
                        </span>
                        <span className="cd-member-score"><FaEye /> {m.access_count || 0}</span>
                      </div>
                      <div className="cd-member-meta">
                        {m.source_name && <span className="cd-member-source"><FaDatabase /> {m.source_name}</span>}
                        <span className="cd-member-id"><FaFingerprint /> {m.learning_id?.slice(0, 8)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {membersPagination.total_pages > 1 && (
                  <div className="cd-pagination">
                    <Button size="sm" variant="outline-secondary" disabled={membersPage <= 1} onClick={() => setMembersPage(p => p - 1)}>← Prev</Button>
                    <span>Page {membersPage} of {membersPagination.total_pages}</span>
                    <Button size="sm" variant="outline-secondary" disabled={membersPage >= membersPagination.total_pages} onClick={() => setMembersPage(p => p + 1)}>Next →</Button>
                  </div>
                )}
              </>
            ) : (
              <div className="cd-empty">No members found</div>
            )}
          </div>
        </Tab>

        {/* HEALTH TAB */}
        <Tab eventKey="health" title={<><FaHeart className="me-1" /> Health</>}>
          <div className="cd-tab-content">
            {healthLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : healthReport ? (
              <>
                <div className="cd-health-header">
                  <div className="cd-health-score" style={{ color: scoreColor(healthReport.health_score / 100) }}>
                    {healthReport.health_score}
                  </div>
                  <div className="cd-health-rating">{healthReport.health_rating}</div>
                </div>

                {healthReport.issues?.length > 0 && (
                  <div className="cd-health-issues">
                    {healthReport.issues.map((issue, i) => (
                      <div key={i} className="cd-health-issue">
                        <FaExclamationTriangle className="text-warning me-2" />
                        {issue}
                      </div>
                    ))}
                  </div>
                )}

                <Row className="g-3 mt-2">
                  <Col md={4}>
                    <Card className="cd-sub-card">
                      <Card.Header className="cd-sub-header">Problems</Card.Header>
                      <Card.Body className="cd-sub-body">
                        {Object.entries(healthReport.problems || {}).map(([k, v]) => (
                          <div key={k} className="cd-kv-row">
                            <span className="cd-kv-key">{k.replace(/_/g, ' ')}</span>
                            <span className="cd-kv-val" style={{ color: v > 0 ? '#ef4444' : undefined }}>{v}</span>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="cd-sub-card">
                      <Card.Header className="cd-sub-header">Diversity</Card.Header>
                      <Card.Body className="cd-sub-body">
                        {Object.entries(healthReport.diversity || {}).map(([k, v]) => (
                          <div key={k} className="cd-kv-row">
                            <span className="cd-kv-key">{k.replace(/_/g, ' ')}</span>
                            <span className="cd-kv-val">{v}</span>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="cd-sub-card">
                      <Card.Header className="cd-sub-header">Freshness</Card.Header>
                      <Card.Body className="cd-sub-body">
                        <div className="cd-kv-row"><span className="cd-kv-key">Newest</span><span className="cd-kv-val">{formatDate(healthReport.freshness?.newest_learning)}</span></div>
                        <div className="cd-kv-row"><span className="cd-kv-key">Oldest</span><span className="cd-kv-val">{formatDate(healthReport.freshness?.oldest_learning)}</span></div>
                        <div className="cd-kv-row"><span className="cd-kv-key">Avg Age</span><span className="cd-kv-val">{healthReport.freshness?.avg_age_days?.toFixed(0)} days</span></div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : <div className="cd-empty">No health data</div>}
          </div>
        </Tab>

        {/* SCORES TAB */}
        <Tab eventKey="scores" title={<><FaChartBar className="me-1" /> Scores</>}>
          <div className="cd-tab-content">
            {scoreDistLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : scoreDistribution ? (
              <Row className="g-3">
                {scoreDistribution.trust_distribution && (
                  <Col md={4}>
                    <Histogram title="Trust Distribution" buckets={scoreDistribution.trust_distribution.buckets} counts={scoreDistribution.trust_distribution.counts} />
                  </Col>
                )}
                {scoreDistribution.credibility_distribution && (
                  <Col md={4}>
                    <Histogram title="Credibility Distribution" buckets={scoreDistribution.credibility_distribution.buckets} counts={scoreDistribution.credibility_distribution.counts} />
                  </Col>
                )}
                {scoreDistribution.importance_distribution && (
                  <Col md={4}>
                    <Histogram title="Importance Distribution" buckets={scoreDistribution.importance_distribution.buckets} counts={scoreDistribution.importance_distribution.counts} />
                  </Col>
                )}
              </Row>
            ) : <div className="cd-empty">No score distribution data</div>}
          </div>
        </Tab>

        {/* SOURCES TAB */}
        <Tab eventKey="sources" title={<><FaDatabase className="me-1" /> Sources</>}>
          <div className="cd-tab-content">
            {sourceLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : (
              <Row className="g-3">
                <Col md={7}>
                  <h6 className="cd-sub-title">Source Breakdown</h6>
                  {sourceBreakdown && sourceBreakdown.length > 0 ? (
                    <div className="cd-source-list">
                      {sourceBreakdown.map((s, i) => (
                        <div key={i} className="cd-source-row">
                          <div className="cd-source-main">
                            <span className="cd-source-name">{s.display_name || s.source_identifier || 'Unknown'}</span>
                            <Badge className="cd-source-type-badge">{s.source_type}</Badge>
                          </div>
                          <div className="cd-source-stats">
                            <span>{s.learning_count} learnings</span>
                            <span>Trust: {s.source_trust?.toFixed(2) ?? '—'}</span>
                            <span>Cred: {s.avg_credibility?.toFixed(3)}</span>
                            {s.negative_credibility_count > 0 && (
                              <span style={{ color: '#ef4444' }}>{s.negative_credibility_count} neg</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="cd-empty">No source data</div>}
                </Col>
                <Col md={5}>
                  <h6 className="cd-sub-title">Classification Breakdown</h6>
                  {classLoading ? <Spinner size="sm" variant="info" /> : classBreakdown && classBreakdown.length > 0 ? (
                    <div className="cd-class-list">
                      {classBreakdown.map((c, i) => (
                        <div key={i} className="cd-class-row">
                          <span className="cd-class-name">{c.classification}</span>
                          <span className="cd-class-count">{c.count}</span>
                          <div className="cd-class-bar-track">
                            <div className="cd-class-bar-fill" style={{ width: `${(c.count / Math.max(...classBreakdown.map(x => x.count), 1)) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="cd-empty">No classification data</div>}
                </Col>
              </Row>
            )}
          </div>
        </Tab>

        {/* CALIBRATION TAB */}
        <Tab eventKey="calibration" title={<><FaBalanceScale className="me-1" /> Calibration</>}>
          <div className="cd-tab-content">
            {calibrationLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : calibration ? (
              <>
                <div className="cd-cal-stats">
                  <ScoreCard label="Total Calibrations" value={calibration.total_calibrations} format="num" color="#3b82f6" />
                  <ScoreCard label="Avg Peers" value={calibration.avg_peers_evaluated?.toFixed(1)} format="raw" color="#8b5cf6" />
                  <ScoreCard label="Avg Cred Change" value={calibration.avg_credibility_change?.toFixed(4)} format="raw" color={calibration.avg_credibility_change >= 0 ? '#22c55e' : '#ef4444'} />
                  <ScoreCard label="Improved" value={calibration.improved} format="num" color="#22c55e" />
                  <ScoreCard label="Degraded" value={calibration.degraded} format="num" color="#ef4444" />
                  <ScoreCard label="Conflicts Flagged" value={calibration.total_conflicts_flagged} format="num" color="#eab308" />
                </div>

                {calibration.recent_calibrations?.length > 0 && (
                  <>
                    <h6 className="cd-sub-title mt-3">Recent Calibrations</h6>
                    <div className="cd-cal-list">
                      {calibration.recent_calibrations.map((c, i) => (
                        <div key={i} className="cd-cal-row">
                          <span className="cd-cal-id"><FaFingerprint /> {c.learning_id?.slice(0, 8)}</span>
                          <Badge className="cd-cal-trigger">{c.trigger}</Badge>
                          <span>{c.peers} peers</span>
                          <span style={{ color: c.cred_after >= c.cred_before ? '#22c55e' : '#ef4444' }}>
                            {c.cred_before?.toFixed(3)} → {c.cred_after?.toFixed(3)}
                          </span>
                          <span className="cd-cal-date">{formatDate(c.calibrated_at)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : <div className="cd-empty">No calibration data (last 30 days)</div>}
          </div>
        </Tab>

        {/* CONFLICTS TAB */}
        <Tab eventKey="conflicts" title={<><FaExclamationTriangle className="me-1" /> Conflicts</>}>
          <div className="cd-tab-content">
            {conflictsLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : conflicts ? (
              <>
                <div className="cd-conflict-summary">
                  <span><strong>{conflicts.total || 0}</strong> conflicts</span>
                  {conflicts.unresolved > 0 && (
                    <Badge bg="danger">{conflicts.unresolved} unresolved</Badge>
                  )}
                  <span className="text-muted">Last {conflicts.lookback_days} days</span>
                </div>

                {conflicts.conflicts?.length > 0 ? (
                  <div className="cd-conflict-list">
                    {conflicts.conflicts.map((c, i) => (
                      <div key={i} className="cd-conflict-row">
                        <div className="cd-conflict-top">
                          <Badge className={`cd-severity-${c.severity}`}>{c.severity}</Badge>
                          <Badge className="cd-conflict-type">{c.type}</Badge>
                          {c.auto_resolved && <Badge bg="success">Auto-resolved</Badge>}
                          {c.requires_review && <Badge bg="warning" text="dark">Review</Badge>}
                        </div>
                        <div className="cd-conflict-desc">{c.description || 'No description'}</div>
                        <div className="cd-conflict-meta">
                          <span><FaFingerprint /> {c.learning_id_1?.slice(0, 8)} vs {c.learning_id_2?.slice(0, 8)}</span>
                          {c.resolution && <span>Resolution: {c.resolution}</span>}
                          <span>{formatDate(c.detected_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="cd-empty">No conflicts found</div>}
              </>
            ) : <div className="cd-empty">No conflict data</div>}
          </div>
        </Tab>

        {/* TIMELINE TAB */}
        <Tab eventKey="timeline" title={<><FaChartLine className="me-1" /> Timeline</>}>
          <div className="cd-tab-content">
            {timelineLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : timeline && timeline.length > 0 ? (
              <div className="cd-timeline">
                <div className="cd-timeline-bars">
                  {timeline.map((t, i) => {
                    const maxAdded = Math.max(...timeline.map(x => x.added), 1);
                    return (
                      <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{formatDate(t.period)}: {t.added} added, cred {t.avg_credibility?.toFixed(3)}</Tooltip>}>
                        <div className="cd-timeline-col">
                          <div className="cd-timeline-bar" style={{ height: `${(t.added / maxAdded) * 100}%` }} />
                          <div className="cd-timeline-label">{i % 3 === 0 ? formatDate(t.period) : ''}</div>
                        </div>
                      </OverlayTrigger>
                    );
                  })}
                </div>
              </div>
            ) : <div className="cd-empty">No timeline data (last 90 days)</div>}
          </div>
        </Tab>

        {/* EVOLUTION TAB */}
        <Tab eventKey="evolution" title={<><FaHistory className="me-1" /> Evolution</>}>
          <div className="cd-tab-content">
            {evolutionLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : evolution ? (
              <>
                <div className="cd-evo-meta">
                  <div className="cd-kv-row"><span className="cd-kv-key">Birth Date</span><span className="cd-kv-val">{formatDate(evolution.birth_date)}</span></div>
                  <div className="cd-kv-row"><span className="cd-kv-key">Last Evolution</span><span className="cd-kv-val">{formatDate(evolution.last_evolution_date)}</span></div>
                  <div className="cd-kv-row"><span className="cd-kv-key">Evolutions</span><span className="cd-kv-val">{evolution.evolution_count}</span></div>
                  <div className="cd-kv-row"><span className="cd-kv-key">Current Members</span><span className="cd-kv-val">{evolution.current_member_count}</span></div>
                </div>

                {evolution.merged_from?.length > 0 && (
                  <>
                    <h6 className="cd-sub-title mt-3">Merged From</h6>
                    {evolution.merged_from.map((m, i) => (
                      <div key={i} className="cd-merge-row">
                        <span className="cd-merge-label">{m.label || m.cluster_id?.slice(0, 12)}</span>
                        <span>{m.member_count} members</span>
                        <span>{formatDate(m.merged_at)}</span>
                      </div>
                    ))}
                  </>
                )}

                {evolution.evolution_history?.length > 0 && (
                  <>
                    <h6 className="cd-sub-title mt-3">Evolution History</h6>
                    {evolution.evolution_history.map((h, i) => (
                      <div key={i} className="cd-evo-entry">{JSON.stringify(h)}</div>
                    ))}
                  </>
                )}
              </>
            ) : <div className="cd-empty">No evolution data</div>}
          </div>
        </Tab>

        {/* HIERARCHY TAB */}
        <Tab eventKey="hierarchy" title={<><FaSitemap className="me-1" /> Hierarchy</>}>
          <div className="cd-tab-content">
            {hierarchyLoading ? <div className="cd-loading-inline"><Spinner size="sm" variant="info" /> Loading…</div> : hierarchy ? (
              <>
                {hierarchy.parent && (
                  <div className="cd-hier-section">
                    <h6 className="cd-sub-title">Parent Cluster</h6>
                    <div className="cd-hier-node cd-hier-parent">
                      <FaSitemap className="me-2" />
                      <span className="cd-hier-label">{hierarchy.parent.label}</span>
                      <Badge className="cd-tree-badge">{hierarchy.parent.member_count} items</Badge>
                      <span style={{ color: scoreColor(hierarchy.parent.coherence) }}>
                        {pct(hierarchy.parent.coherence)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="cd-hier-section">
                  <h6 className="cd-sub-title">Current Cluster</h6>
                  <div className="cd-hier-node cd-hier-current">
                    <FaProjectDiagram className="me-2 text-info" />
                    <span className="cd-hier-label">{hierarchy.target?.label}</span>
                    <Badge className="cd-tree-badge">{hierarchy.target?.member_count} items</Badge>
                    <span>Level {hierarchy.target?.hierarchy_level}</span>
                  </div>
                </div>

                {hierarchy.children?.length > 0 && (
                  <div className="cd-hier-section">
                    <h6 className="cd-sub-title">Children ({hierarchy.children_count})</h6>
                    {hierarchy.children.map((c, i) => (
                      <div key={i} className="cd-hier-node cd-hier-child">
                        <span className="cd-tree-branch">└</span>
                        <span className="cd-hier-label">{c.label}</span>
                        <Badge className="cd-tree-badge">{c.member_count} items</Badge>
                        <span style={{ color: scoreColor(c.coherence) }}>{pct(c.coherence)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : <div className="cd-empty">No hierarchy data</div>}
          </div>
        </Tab>

      </Tabs>
    </div>
  );
};

export default ClusterDetailView;
